import { all, fork, put, select, call } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { b64ToBlob, convertBlobToBase64, removeStringB64, parseSettings, mergeArraysInObject } from 'heliocor-ui'

import {
    createDocumentData, verifyIndividualDocument, parseCustomFields,
    parserValues, documentValidator, parserCPValues, getParsedCategoriesList
} from '../documentsHelpers'
import { db } from '../../api/db'
import HeliocorStorage from '../../config/HeliocorStorage'
import { phaseTypes } from '../../config/phaseTypes.enum'
import { takeLatestSafe } from '../sagasHelper';

import {
    ACTIVATE_CAFE,
    DELETE_ENTITY,
    DELETE_LOOKUP_SUGGESTION,
    DELETE_STORED_DOCUMENT,
    REQUEST_ACTIVATION,
    REQUEST_CHECK_ACTIVATION_DATA,
    REQUEST_CHECK_PHASE,
    REQUEST_DATAS_FROM_OWNER_DOCS,
    REQUEST_DELETE_FILE_FROM_DOC,
    REQUEST_DELETE_OWNER,
    REQUEST_DELETE_STORED_DOCUMENT,
    REQUEST_GET_PERSONALIZATION,
    REQUEST_GET_SETTINGS,
    REQUEST_INIT_PREAPPROVAL,
    REQUEST_INIT_REQUEST,
    REQUEST_INSERT_OWNER,
    REQUEST_OWNERS,
    REQUEST_REQUESTED_ENTITY,
    REQUEST_RESET_CUSTOMER_DATA,
    REQUEST_SAVE_DATA_TO_LOCALSTORE,
    REQUEST_SEND_ACTIVATION_EMAIL,
    REQUEST_SEND_DATA_PREAPPROVAL,
    REQUEST_SEND_DOC_FOR_MANUAL_VALIDATION,
    REQUEST_SEND_DOC_TO_SERVICE,
    REQUEST_SET_NAVIGATION,
    REQUEST_STORE_DOCUMENT,
    REQUEST_SUBMIT_REQUEST,
    REQUEST_UPDATE_OWNER,
    REQUEST_UPDATE_REQUESTED_ENTITY,
    REQUEST_UPLOAD_ENTITY_DOC,
    RESET_ACTIVATION,
    SET_BACKUP_PARTY,
    REQUEST_BACKUP_PARTY,
    SET_CAFE_STATUS,
    SET_CLIENT_EMAIL,
    SET_CP_FIELDS,
    SET_CURRENT_OWNER_DOC,
    SET_CURRENT_OWNER_DOCS,
    SET_CUSTOM_FIELDS,
    SET_DOCUMENT_RETRIES,
    SET_INIT_APP,
    SET_IS_REQUEST,
    SET_NAVIGATION,
    SET_NOTIFICATION,
    SET_ONBOARDING_TYPE,
    SET_OWNERS,
    SET_PHASE_TYPE,
    SET_SETTINGS,
    SET_SUPPORTED_COUNTRIES,
    SET_SUPPORTED_REGIONS,
    SET_TOKEN_VERIFICATION,
    STORE_DOCUMENT,
    STORE_DOCUMENTS,
    STORE_PARTY_DATA,
    UPDATE_OWNERS,
    SET_POST_ERRORS,
    SET_STEP_INDEX,
    SET_FIELD_CATEGORIES,
    SET_CP_CATEGORIES,
    REQUEST_CUSTOM_FIELDS,
    REQUEST_DOCUMENTS,
    REQUEST_UPDATE_KYC_TYPE,
    SAVE_DOCUMENT
} from './actionTypes'
import { getPartyPath } from '../../common/Wizard/helpers';
import { getParsedDependencies } from '../../utils/parsers';

export function parseSingleSetting( res ) {
    let prop = res?.setting?.value
    return prop ? JSON.parse(prop) : {}
}

const watcher = () => function* watch() {
    yield takeLatestSafe(REQUEST_GET_SETTINGS, getSettings)
    yield takeLatestSafe(REQUEST_CUSTOM_FIELDS, requestCustomFields)
    yield takeLatestSafe(REQUEST_DOCUMENTS, requestDocuments)
    yield takeLatestSafe(REQUEST_CHECK_PHASE, checkPhase)
    yield takeLatestSafe(REQUEST_ACTIVATION, requestActivation)
    yield takeLatestSafe(REQUEST_RESET_CUSTOMER_DATA, resetCustomerData)
    yield takeLatestSafe(REQUEST_SEND_ACTIVATION_EMAIL, sendActivationEmail)
    yield takeLatestSafe(REQUEST_SEND_DOC_TO_SERVICE, sendDocToService)
    yield takeLatestSafe(REQUEST_SAVE_DATA_TO_LOCALSTORE, saveDataToLocalStore)
    yield takeLatestSafe(SAVE_DOCUMENT, saveDocument)
    yield takeLatestSafe(REQUEST_SEND_DATA_PREAPPROVAL, sendDataPreApproval)
    yield takeLatestSafe(REQUEST_GET_PERSONALIZATION, getPersonalization)
    yield takeLatestSafe(REQUEST_CHECK_ACTIVATION_DATA, checkActivationData)
    yield takeLatestSafe(REQUEST_DELETE_STORED_DOCUMENT, deleteStoredDocument)
    yield takeLatestSafe(REQUEST_DELETE_FILE_FROM_DOC, deleteFileFromDoc)
    yield takeLatestSafe(REQUEST_SEND_DOC_FOR_MANUAL_VALIDATION, sendDocForManualValidation)
    yield takeLatestSafe(REQUEST_REQUESTED_ENTITY, runRequestRequestedEntity)
    yield takeLatestSafe(REQUEST_STORE_DOCUMENT, storeDocument)
    yield takeLatestSafe(REQUEST_UPLOAD_ENTITY_DOC, runRequestUploadEntityDocuments)
    yield takeLatestSafe(REQUEST_INSERT_OWNER, runRequestInsertOwner)
    yield takeLatestSafe(REQUEST_UPDATE_OWNER, runUpdateOwner)
    yield takeLatestSafe(REQUEST_DELETE_OWNER, runRequestDeleteOwner)
    yield takeLatestSafe(REQUEST_OWNERS, runRequestOwners)
    yield takeLatestSafe(REQUEST_SUBMIT_REQUEST, runRequestSubmitRequest)
    yield takeLatestSafe(REQUEST_INIT_REQUEST, requestInit)
    yield takeLatestSafe(REQUEST_INIT_PREAPPROVAL, preApprovalInit)
    yield takeLatestSafe(REQUEST_DATAS_FROM_OWNER_DOCS, runRequestDatasFromOwnerDocs)
    yield takeLatestSafe(REQUEST_UPDATE_REQUESTED_ENTITY, runRequestUpdateRequestedEntity)
    yield takeLatestSafe(REQUEST_SET_NAVIGATION, saveNavigationToLocalStorage)
    yield takeLatestSafe(REQUEST_BACKUP_PARTY, requestBackupParty)
    yield takeLatestSafe(REQUEST_UPDATE_KYC_TYPE, runRequestUpdateKycType)
}

const getPartyType = state => state.config.partyType
const getAuthenticationStatus = state => state.config.authenticated
const getParty = state => state.party
const getCPs = state => state.party.connectedParties
const getClientEmail = state => state.config.email
const getDocuments = state => state.party.documents
const getNavigation = state => state.navigation
const getRetries = state => state.party.maxRetries
const getPhase = state => state.config.phase
const getIsRequest = state => state.config.isRequest
const getOrg = state => state.config.org
const getFields = state => state.settings.customFields
const getCPFields = state => state.settings.cpFields
const getCategories = state => state.settings.fieldCategories
const getCurrentOwnerDocsToDelete = state => state.currentOwnerDocsToDelete || []
const getFormattingOptions = state => state.settings.formattingOptions
const getKYCType = state => state.settings.kycType
const getDocValidations = state => state.party.docValidations

function* deleteStoredDocument({ docType }) {

    const documents = yield select(getDocuments)
    let dokstorDocsBackUp = yield HeliocorStorage.getItem('dokstorCafeDocs')
    const isRequest = yield select(getIsRequest)
    const partyType = yield select(getPartyType)
    const partyPath = getPartyPath(partyType)

    if (isRequest) {
        yield call(deleteAllFilesFromDoc, { docType })
    } else {
        const body = { type: docType }
        yield db('post', `dokstor-cafe-api/api/self-onboarding/${partyPath}/documents/delete`, body)

        if (dokstorDocsBackUp) {

            let docsToSaveLocally = JSON.parse(dokstorDocsBackUp)

            if (!isEmpty(documents)) {

                let docToRemove = docsToSaveLocally.findIndex(doc => doc.type === docType)
                if (docToRemove !== -1) docsToSaveLocally.splice(docToRemove, 1)

                if (isEmpty(docsToSaveLocally)) {
                    yield HeliocorStorage.removeItem('dokstorCafeDocs')
                } else if (dokstorDocsBackUp) {
                    yield HeliocorStorage.setItem('dokstorCafeDocs', JSON.stringify(docsToSaveLocally))
                }
            }
        }

        yield put({ type: DELETE_STORED_DOCUMENT, document: docType })
    }

}

function* deleteAllFilesFromDoc({ docType }) {

    const documents = yield select(getDocuments)
    const partyType = yield select(getPartyType)
    const partyPath = getPartyPath(partyType)

    const updateDocsToStore = cloneDeep(documents)
    const docToEdit = updateDocsToStore.find(doc => doc.type === docType)
    const docToEditIndex = updateDocsToStore.findIndex(doc => doc.type === docType)

    if (docToEdit && docToEdit.datas) {
        db('delete', `dokstor-cafe-api/api/request/${partyPath}/documents/${docToEdit.identifier}/datas`)
        updateDocsToStore[docToEditIndex].datas = []
        yield put({ type: STORE_DOCUMENTS, docs: updateDocsToStore })
    }
}

function* deleteFileFromDoc({ document }) {

    const partyType = yield select(getPartyType)
    const partyPath = getPartyPath(partyType)

    const isRequest = yield select(getIsRequest)

    const { docType, index, identifier } = document
    const documents = yield select(getDocuments)

    let dokstorDocsBackUp = yield HeliocorStorage.getItem('dokstorCafeDocs')

    const updateDocsToStore = cloneDeep(documents)
    const docToEditIndex = updateDocsToStore.findIndex(doc => doc.type === docType)

    if (isRequest) {
        const dataToDelete = updateDocsToStore[docToEditIndex].datas.find(d => d.index === index)
        const response = yield db('delete', `dokstor-cafe-api/api/request/${partyPath}/documents/${identifier}/datas/${dataToDelete.identifier}`)
        if (response && !response.errorCode) {
            const newIndex = updateDocsToStore[docToEditIndex].datas.findIndex(d => d.index === index)
            updateDocsToStore[docToEditIndex].datas.splice(newIndex, 1)
        }
    } else {
        const body = {type: docType, index}
        const response = yield db('post', `dokstor-cafe-api/api/self-onboarding/${partyPath}/documents/delete`, body)
        if (response && !response.errorCode) {
            updateDocsToStore[docToEditIndex].datas.splice(index, 1)
            !updateDocsToStore[docToEditIndex].datas.length && delete updateDocsToStore.splice(docToEditIndex, 1)
        }
    }

    if (dokstorDocsBackUp && !isRequest) {
        const docsOnLocalStorage = JSON.parse(dokstorDocsBackUp)
        let docToEditIndex = docsOnLocalStorage.findIndex(doc => doc.type === docType)
        if (docToEditIndex !== -1) {
            docsOnLocalStorage[docToEditIndex].datas.splice(index, 1)
            !docsOnLocalStorage[docToEditIndex].datas.length && delete docsOnLocalStorage.splice(docToEditIndex, 1)
            yield HeliocorStorage.setItem('dokstorCafeDocs', JSON.stringify(docsOnLocalStorage))
        }

    }

    yield put({ type: STORE_DOCUMENTS, docs: updateDocsToStore })
}

function* getPersonalization({ alreadyAuthenticated }) {

    let settings = { customLogo: null }

    const orgId = yield select(getOrg)
    const settingResponse = yield db('get', `dokstor-cafe-api/api/authentication/settings/${orgId}/cafe/personalisation`)
    const cafePersonalization = parseSingleSetting(settingResponse)

    if (isEmpty(cafePersonalization)) {
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.authSuccess', type: 'success' } })
    } else {
        if (cafePersonalization.LOGO_IMAGE) {
            const blob = yield b64ToBlob(cafePersonalization.LOGO_IMAGE)
            settings.customLogo = yield window.URL.createObjectURL(blob)
        }

        if (cafePersonalization.SUPPORT_EMAIL) settings.supportEmail = cafePersonalization.SUPPORT_EMAIL

        const { mainColor, mainText, stepperBackground, secundaryColor, backgroundColor } = cafePersonalization
        settings.colors = { mainColor, mainText, stepperBackground, secundaryColor, backgroundColor }
        settings.otp = settingResponse?.otp
        settings.termsAndConditions = settingResponse?.termsAndConditions
        settings.languages = settingResponse?.languages
        settings.showYapilyStep = settingResponse?.personBankValidation

        if (!alreadyAuthenticated) yield put({ type: SET_INIT_APP, appIsReady: true })
        yield put({ type: SET_SETTINGS, settings })
    }
}

function* getSettings() {

    const isRequest = yield select(getIsRequest)
    const phase = yield select(getPhase)
    const partyType = yield select(getPartyType)
    const isCompany = partyType === "COMPANY" || partyType === "CONNECTED_COMPANY"

    const url = new URL(window.location.href)
    const kycType = url.searchParams.get("kyc-type-id")

    let previousPartyDetailsFields = {}

    const settingsKeys = ['CAFE_SETTINGS', 'PERSON_CAFE', 'COMPANY_CAFE', 'CAFE_KYC_SETTINGS_PERSON', 'CAFE_KYC_SETTINGS_COMPANY']

    yield put({ type: REQUEST_GET_PERSONALIZATION, alreadyAuthenticated: true })

    const settingsResponse = yield db('get', `dokstor-cafe-api/api/${phase}/settings/cafe/key?&keys=${settingsKeys.join('&keys=')}`)

    const formattingResponse = yield db('get', `dokstor-cafe-api/api/${phase}/settings/formatter`)
    const formattingOptions = !formattingResponse?.errors && JSON.parse(formattingResponse.value)

    let kycTypeResponse, SOUniqueKYC
    if (isRequest) {
        kycTypeResponse = yield db('get', `dokstor-cafe-api/api/${phase}/settings/kyc?type=${partyType}${kycType ? `&kyc-type-id=${kycType}` : ''}`)
    } else {
        kycTypeResponse = yield db('get', `dokstor-cafe-api/api/self-onboarding/settings/kyc-types?type=${partyType}`)

        SOUniqueKYC = kycTypeResponse.kycTypes.length === 1
    }
 
    const cafeSettings = parseSettings(settingsResponse, 'CAFE_SETTINGS')
    const companyCafeSettings = parseSettings(settingsResponse, 'COMPANY_CAFE')
    const personCafeSettings = parseSettings(settingsResponse, 'PERSON_CAFE')
    const personKYCSettings = parseSettings(settingsResponse, 'CAFE_KYC_SETTINGS_PERSON')
    const companyKYCSettings = parseSettings(settingsResponse, 'CAFE_KYC_SETTINGS_COMPANY')

    let kycTypeId

    if (isRequest || SOUniqueKYC) {
        kycTypeId = isRequest ? kycTypeResponse.kycConfiguration?.kycType : kycTypeResponse.kycTypes[0].identifier
        yield put({ type: REQUEST_CUSTOM_FIELDS, kycTypeId })
    }

    const includeDefaultKycDocsCompany = companyCafeSettings?.INCLUDE_DEFAULT_KYC_DOCS
    const includeStructure = companyCafeSettings?.INCLUDE_STRUCTURE
    const includeDefaultKycDocsPerson = personCafeSettings?.INCLUDE_DEFAULT_KYC_DOCS

    const checkHTTP = new RegExp('^(http|https)')

    let urlOk = false

    if (cafeSettings && cafeSettings.URL_OK && cafeSettings.URL_OK !== '') urlOk = checkHTTP.test(cafeSettings.URL_OK)
        ? cafeSettings.URL_OK : `http://${cafeSettings.URL_OK}`
    
    const settings = {
        urlOk: urlOk,
        selfieMustBeTakenWithCamera: personCafeSettings && personCafeSettings.SELFIE_MUST_BE_CAMERA,
        formattingOptions
    }
    
    if (personCafeSettings) {
        settings.statementRecordingTime = personCafeSettings.STATEMENT_RECORDING_TIME
        settings.statementCheckMessage = personCafeSettings.STATEMENT_CHECK_MESSAGE
        settings.imageSelfieInstructions = personCafeSettings.SELFIE_INTRUCTIONS
        settings.personKYCSettings = personKYCSettings
        settings.companyKYCSettings = companyKYCSettings
    }

    
    if (isRequest) {

        yield put({ type: REQUEST_DOCUMENTS, kycType: kycTypeResponse })

        const preRequestCategoryFields = yield db('get', `dokstor-cafe-api/api/${phase}/settings/fields?type=${partyType}&channel=CAFE_PHASE_1`)

        preRequestCategoryFields.categories.sort((a, b) => {
            if (!a.order) return 1;
            return parseFloat(a.order) - parseFloat(b.order)
        })

        preRequestCategoryFields.categories
            .forEach((cat, i) => {
                previousPartyDetailsFields[cat.code || cat.name] = cat.fields
                    .map(field => {
                        const { internalName, valueType, values, name, description } = field
                        return ({
                            id: internalName,
                            type: valueType,
                            label: internalName,
                            placeHolder: internalName,
                            customLabel: name,
                            customOptions: values,
                            description
                        })
                    })
            })

        yield put({
            type: SET_SETTINGS,
            settings: {
                ...settings,
                previousPartyDetailsFields,
                entity: partyType,
                kycType: kycType || kycTypeId,
                kycTypes: kycTypeResponse.kycTypes,
            }
        })

    } else {

        if (SOUniqueKYC && ((partyType === 'COMPANY' && includeDefaultKycDocsCompany) || (partyType === 'PERSON' && includeDefaultKycDocsPerson))) {
            yield put({ type: REQUEST_DOCUMENTS, kycTypeId: kycTypeResponse.kycTypes[0].identifier })
        }

        yield put({
            type: SET_SETTINGS,
            settings: {
                ...settings,
                kycType: kycType || kycTypeId,
                entity: partyType,
                includeStructure,
                kycTypes: kycTypeResponse.kycTypes
            },
            retries: isCompany ? companyCafeSettings?.RETRIES : personCafeSettings?.RETRIES
        })
    }

    yield put({ type: SET_INIT_APP, appIsReady: true })
    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'PARTY_LOADED' })
}

function* requestDocuments({ kycType, kycTypeId }) {

    const isRequest = yield select(getIsRequest)
    const phase = yield select(getPhase)
    const partyType = yield select(getPartyType)

    if (isRequest) {

        let documentTypes
        const partyPath = getPartyPath(partyType)
        const request = yield db('get', `dokstor-cafe-api/api/request/${partyPath}/request`)

        const preRequestCategoryFields = yield db('get', `dokstor-cafe-api/api/${phase}/settings/fields?type=${partyType}&channel=CAFE_PHASE_1`)

        preRequestCategoryFields.categories.sort((a, b) => {
            if (!a.order) return 1;
            return parseFloat(a.order) - parseFloat(b.order)
        })

        if (!request.request) {

            yield put({ type: SET_NOTIFICATION, notification: { text: `warning.${kycType.errorCode}`, type: 'error' } })

        } else if (partyType === 'COMPANY' || partyType === 'CONNECTED_COMPANY') {

            const requestData = request.request

            const companyDocuments = requestData.documentTypes || []
            const authSignatoryDocuments = requestData.authSignatoryDocumentTypes || []
            const directorDocuments = requestData.directorDocumentTypes || []
            const shareholderDocuments = requestData.shareholderDocumentTypes || []
            const uboDocuments = requestData.uboDocumentTypes || []
            const companyProfile = requestData.profile
            const structure = requestData.structure

            const companyDocsCategories = kycType.kycConfiguration.entityTypes.filter(element => element.code === "COMPANY")[0].categories
            const companyDocCategoryNames = companyDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})
            const authSignatoryDocsCategories = kycType.kycConfiguration.entityTypes.filter(element => element.code === "DIRECTOR")[0].categories
            const authSignatoryDocCategoryNames = authSignatoryDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})
            const directorDocsCategories = kycType.kycConfiguration.entityTypes.filter(element => element.code === "SHAREHOLDER")[0].categories
            const directorDocCategoryNames = directorDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})
            const shareholderDocsCategories = kycType.kycConfiguration.entityTypes.filter(element => element.code === "AUTH_SIGNATORY")[0].categories
            const shareholderDocCategoryNames = shareholderDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})
            const uboDocsCategories = kycType.kycConfiguration.entityTypes.filter(element => element.code === "UBO")[0].categories
            const uboDocCategoryNames = uboDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})

            if (!companyProfile) {
                yield put({ type: SET_CUSTOM_FIELDS, customFields: {} })
            }

            const companyDocsRequested = {}
            const authSignatoryDocsRequested = {}
            const directorDocsRequested = {}
            const shareholderDocsRequested = {}
            const uboDocsRequested = {}

            companyDocuments.map(item => companyDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))
            authSignatoryDocuments.map(item => authSignatoryDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))
            directorDocuments.map(item => directorDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))
            shareholderDocuments.map(item => shareholderDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))
            uboDocuments.map(item => uboDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))

            const companyDocumentList = Object.values(companyDocsRequested).reduce((acc, val) => acc.concat(val), [])
            const authSignatoryDocumentList = Object.values(authSignatoryDocsRequested).reduce((acc, val) => acc.concat(val), [])
            const directorDocumentList = Object.values(directorDocsRequested).reduce((acc, val) => acc.concat(val), [])
            const shareholderDocumentList = Object.values(shareholderDocsRequested).reduce((acc, val) => acc.concat(val), [])
            const uboDocumentList = Object.values(uboDocsRequested).reduce((acc, val) => acc.concat(val), [])

            const requested = partyType === 'COMPANY'
                ? yield db('get', `dokstor-cafe-api/api/request/settings/documents?&names=${companyDocumentList.join('&names=')}&names=${authSignatoryDocumentList.join('&names=')}&names=${directorDocumentList.join('&names=')}&names=${shareholderDocumentList.join('&names=')}&names=${uboDocumentList.join('&names=')}`)
                : yield db('get', `dokstor-cafe-api/api/request/settings/documents?&names=${companyDocumentList.join('&names=')}`)

            documentTypes = Object.keys(companyDocsRequested).map((key) => ({
                name: companyDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.documentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: key === "OTHERS"
                    ? companyDocsRequested[key].map((doc, i) => ({
                        name: doc,
                        extensions: ['JPEG', 'JPG', 'PNG', 'PDF'],
                        order: i,
                        filesNumber: 'N'
                    }))
                    : requested.documents.filter(reqDoc => companyDocsRequested[key].includes(reqDoc.name))
            }))

            const authSignatoryDocumentTypes = Object.keys(authSignatoryDocsRequested).map((key) => ({
                name: authSignatoryDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.authSignatoryDocumentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: requested.documents.filter(reqDoc => authSignatoryDocsRequested[key].includes(reqDoc.name))
            }))

            const directorDocumentTypes = Object.keys(directorDocsRequested).map((key) => ({
                name: directorDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.directorDocumentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: requested.documents.filter(reqDoc => directorDocsRequested[key].includes(reqDoc.name))
            }))

            const shareholderDocumentTypes = Object.keys(shareholderDocsRequested).map((key) => ({
                name: shareholderDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.shareholderDocumentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: requested.documents.filter(reqDoc => shareholderDocsRequested[key].includes(reqDoc.name))
            }))

            const uboDocumentTypes = Object.keys(uboDocsRequested).map((key) => ({
                name: uboDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.uboDocumentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: requested.documents.filter(reqDoc => uboDocsRequested[key].includes(reqDoc.name))
            }))

            const companyDocs = kycType.kycConfiguration.entityTypes.find(docs => docs.code === 'COMPANY')

            yield put({
                type: SET_SETTINGS,
                settings: {
                    data: documentTypes, // This is for both company docs and individual docs
                    minSuggestedDirector: kycType.kycConfiguration.directorsNumber,
                    minShareholderPercent: kycType.kycConfiguration.shareholdersPercent,
                    authSignatoryDocumentTypes,
                    directorDocumentTypes,
                    shareholderDocumentTypes,
                    uboDocumentTypes,
                    structure,
                    entity: kycType.kycConfiguration.entityTypes[0].code,
                    showCategories: requestData.data?.showCategories,
                    showFields: requestData.data?.showFields,
                    dataParties: [companyDocs]
                },
            })
        } else {
            const requestData = request.request

            const personDocsRequested = {}

            const personDocuments = requestData.documentTypes || []
            const personDocsCategories = kycType.kycConfiguration.entityTypes[0].categories
            const personDocCategoryNames = personDocsCategories.reduce((acc, val) => ({ ...acc, [val.code]: val.name }), {})
            const personProfile = requestData.profile

            if (!personProfile) {
                yield put({ type: SET_CUSTOM_FIELDS, customFields: {} })
            }

            personDocuments.map(item => personDocsRequested[item.categoryName] = item.documents.map(doc => doc.type))

            const personDocumentList = Object.values(personDocsRequested).reduce((acc, val) => acc.concat(val), [])
            const requested = yield db('get', `dokstor-cafe-api/api/request/settings/documents?&names=${personDocumentList.join('&names=')}`)

            documentTypes = Object.keys(personDocsRequested).map((key) => ({
                name: personDocCategoryNames[key] || key,
                code: key,
                minDocuments: requestData.documentTypes.filter(doc => doc.categoryName === key)[0].minDocuments,
                documents: key === 'OTHERS'
                    ? personDocsRequested[key].map((doc, i) => ({
                        name: doc,
                        extensions: ['JPEG', 'JPG', 'PNG', 'PDF'],
                        order: i,
                        filesNumber: 'N'
                    }))
                    : requested.documents.filter(reqDoc => personDocsRequested[key].includes(reqDoc.name))
            }))

            yield put({
                type: SET_SETTINGS,
                settings: {
                    kycType: kycType.kycConfiguration.kycType,
                    entity: kycType.kycConfiguration.entityTypes[0].code,
                    data: documentTypes, // This is for both company docs and individual docs
                    showCategories: requestData.data?.showCategories,
                    showFields: requestData.data?.showFields
                }
            })
        }
    } else {

        const kycTypeResponse = yield db('get', `dokstor-cafe-api/api/self-onboarding/settings/kyc?type=${partyType}${kycTypeId ? `&kyc-type-id=${kycTypeId}` : ''}`)

        let documents = []
        let dataParties = []

        if (partyType === 'COMPANY') {
            let companyDocsExist = kycTypeResponse.kycConfiguration.entityTypes.some(docs => docs.code === 'COMPANY')
            let companyDocuments = kycTypeResponse.kycConfiguration.entityTypes.filter(docs => docs.code === 'COMPANY')[0]

            documents = companyDocsExist && companyDocuments.categories
                ? companyDocuments.categories.sort((a, b) => a.order - b.order) : []

            const _uboDocs = kycTypeResponse.kycConfiguration.entityTypes.find(docs => docs.code === 'UBO')
            const _authDocs = kycTypeResponse.kycConfiguration.entityTypes.find(docs => docs.code === 'AUTH_SIGNATORY')
            const _directorDocs = kycTypeResponse.kycConfiguration.entityTypes.find(docs => docs.code === 'DIRECTOR')
            const _shareholderDocs = kycTypeResponse.kycConfiguration.entityTypes.find(docs => docs.code === 'SHAREHOLDER')
            const _companyDocs = kycTypeResponse.kycConfiguration.entityTypes.find(docs => docs.code === 'COMPANY')

            dataParties = [
                _uboDocs || [],
                _authDocs || [],
                _directorDocs || [],
                _shareholderDocs || [],
                _companyDocs || []
            ]
        }

        if (partyType === 'PERSON') {
            documents = kycTypeResponse.kycConfiguration.entityTypes[0].categories?.sort((a, b) => a.order - b.order) || []
        }

        yield put({
            type: SET_SETTINGS,
            settings: {
                minSuggestedDirector: kycTypeResponse.kycConfiguration.directorsNumber,
                minShareholderPercent: kycTypeResponse.kycConfiguration.shareholdersPercent,
                data: documents,
                dataParties,
            }
        })
    }
}

function* requestCustomFields({ kycTypeId }) {

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const isRequest = yield select(getIsRequest)
    const phase = yield select(getPhase)
    const partyType = yield select(getPartyType)
    const formattingOptions = yield select(getFormattingOptions)

    const endpoint = isRequest
        ? `dokstor-cafe-api/api/${phase}/settings/fields?type=${partyType}&channel=CAFE_PHASE_2`
        : `dokstor-cafe-api/api/${phase}/settings/fields?type=${partyType}`

    const customFieldsSettings = yield db('get', endpoint)
    const orderedCustomFields = customFieldsSettings?.categories?.sort((a, b) => a.order < b.order)
    const fieldCategories = orderedCustomFields
        .map(({ identifier, code, name, description, dependencies = '{}' }) => ({
            identifier,
            code,
            name,
            description,
            dependencies: getParsedDependencies(dependencies)
        }))
    const customFields = parseCustomFields(orderedCustomFields, phase, formattingOptions, kycTypeId, ['CONNECTED_PERSON', 'CONNECTED_COMPANY'].includes(partyType))

    yield put({ type: SET_CUSTOM_FIELDS, customFields })
    yield put({ type: SET_FIELD_CATEGORIES, fieldCategories })

    if (partyType === 'COMPANY') {
        const connectedPersonFieldsFromAPI = yield db('get', `dokstor-cafe-api/api/${phase}/settings/fields?type=CONNECTED_PERSON${isRequest ? '&channel=CAFE_PHASE_2' : ''}`)
        const connectedCompanyFieldsFromAPI = yield db('get', `dokstor-cafe-api/api/${phase}/settings/fields?type=CONNECTED_COMPANY${isRequest ? '&channel=CAFE_PHASE_2' : ''}`)

        const personFieldsByCategory = parseCustomFields(connectedPersonFieldsFromAPI.categories, phase, formattingOptions, kycTypeId, true)
        const companyFieldsByCategory = parseCustomFields(connectedCompanyFieldsFromAPI.categories, phase, formattingOptions, kycTypeId, true)

        const connectedPersonFields = mergeArraysInObject(personFieldsByCategory)
        const connectedCompanyFields = mergeArraysInObject(companyFieldsByCategory)

        const connectedPersonCategories = getParsedCategoriesList(connectedPersonFieldsFromAPI.categories, personFieldsByCategory)
        const connectedCompanyCategories = getParsedCategoriesList(connectedCompanyFieldsFromAPI.categories, companyFieldsByCategory)

        yield put({ type: SET_CP_FIELDS, fields: { CONNECTED_PERSON: connectedPersonFields, CONNECTED_COMPANY: connectedCompanyFields } })
        yield put({ type: SET_CP_CATEGORIES, categories: { CONNECTED_PERSON: connectedPersonCategories, CONNECTED_COMPANY: connectedCompanyCategories } })
    }

    if (partyType === 'COMPANY') {
        const res = yield db('get', `dokstor-cafe-api/api/search/companies/search-countries`)

        yield put({ type: SET_SUPPORTED_COUNTRIES, supportedCountries: res.countries })
        yield put({ type: SET_SUPPORTED_REGIONS, supportedRegions: res.regions })
    }

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'PARTY_LOADED' })
}

function* saveDocument({ doc }) {
    const navigation = yield select(getNavigation)
    const docValidations = yield select(getDocValidations)
    const _partyType = yield select(getPartyType)
    const partyType = _partyType === "COMPANY" ? 'companies' : 'persons'

    let documentToSave
    
    const { type, datas, category, ...metadata } = doc

    if (datas) {
        const datas = yield Promise.all(doc.datas.map(async data => {
            const content = await convertBlobToBase64(data.content)
            return { index: data.index, content: removeStringB64(content) }
        }))
        documentToSave = { type: doc.type, datas, category, metadata: JSON.stringify(metadata) }
    }

    const body = {
        documents: [documentToSave],
        navigation: JSON.stringify({...navigation, docValidations})
    }

    const res = yield db('put', `dokstor-cafe-api/api/self-onboarding/${partyType}/documents`, body)

    const responseErrors = res && ((res.errors && res.errors.map(err => err.code)) || res.errorCode)

    if (responseErrors) {
        const responseErrors = res?.errors?.map(err => err.code)
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
    } else {
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
    }
}

function* saveDataToLocalStore({ stepType, cat, index }) {
    const party = yield select(getParty)
    const navigation = yield select(getNavigation)
    const fields = yield select(getFields)
    const categories = yield select(getCategories)
    const isRequest = yield select(getIsRequest)
    const kycType = yield select(getKYCType)
    const docValidations = yield select(getDocValidations)

    const _partyType = yield select(getPartyType)
    const partyType = _partyType === "COMPANY" ? 'companies' : 'persons'

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const newStepIndex = index || navigation.stepIndex + 1

    const { connectedParties, data } = party

    //#region FIND A METHOD TO AVOID 3 CONSECUTIVES MAPS TODO... 
    if (!isEmpty(connectedParties) && connectedParties) {
        yield all(connectedParties.map(async owner => {
            if (owner.documents) {
                const documents = await Promise.all(owner.documents.map(async doc => {
                    if (doc.datas) {
                        const datas = await Promise.all(doc.datas.map(async data => {
                            const content = await convertBlobToBase64(data.content)
                            return { index: data.index, content: removeStringB64(content) }
                        }))
                        return { ...doc, datas }
                    }
                    return doc
                }))
                return { ...owner, documents }
            }
            return owner
        }))
    }

    if (!isEmpty(connectedParties) && connectedParties.connectedCompanies) {
        yield all(connectedParties.connectedCompanies.map(async owner => {
            if (owner.documents) {
                const documents = await Promise.all(owner.documents.map(async doc => {
                    if (doc.datas) {
                        const datas = await Promise.all(doc.datas.map(async data => {
                            const content = await convertBlobToBase64(data.content)
                            return { index: data.index, content: removeStringB64(content) }
                        }))
                        return { ...doc, datas }
                    }
                    return doc
                }))
                return { ...owner, documents }
            }
            return owner
        }))
    }
    //#endregion

    const category = categories?.find(c => cat === (c.code || c.name) )?.identifier

    let res

    const backupNavigation = JSON.stringify({
        stepIndex: newStepIndex,
        completedSteps: [...new Set([...navigation.completedSteps, navigation.stepIndex])],
        backupStepIndex: newStepIndex,
        docValidations
    })

    if (!isRequest) {
        if (stepType === 'fields') {
            const parsedData = parserValues(fields, data)
            const body = {
                ...parsedData,
                category,
                navigation: backupNavigation,
                externalIdentifier: data.externalIdentifier,
                kycType
            }
            res = yield db('put', `dokstor-cafe-api/api/self-onboarding/${partyType}/fields`, body)

        } else if (stepType === 'documents') {
            const body = {
                documents: [],
                navigation: backupNavigation
            }
            res = yield db('put', `dokstor-cafe-api/api/self-onboarding/${partyType}/documents`, body)
        }
    }

    const hasPostErrors = res?.errors?.some(e => e.field)
    const responseErrors = res && ((res.errors && res.errors.map(err => err.code)) || res.errorCode)
    
    if (hasPostErrors) {
        yield put({ type: SET_POST_ERRORS, errors: res.errors })
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'POST_ERRORS' })
    } else if (responseErrors) {
        const responseErrors = res?.errors?.map(err => err.code)
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
    } else {
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
        yield put({ type: SET_STEP_INDEX, stepIndex: newStepIndex, completedStep: navigation.stepIndex })
    }

    yield HeliocorStorage.setItem({ key: 'dokstorCafeNavigation', value: navigation })
}

function* saveNavigationToLocalStorage() {

    const navigation = yield select(getNavigation)
    const docValidations = yield select(getDocValidations)
    const isRequest = yield select(getIsRequest)
    const navToSave = { ...navigation, backupStepIndex: navigation.stepIndex, docValidations }

    const item = isRequest ? 'req_dokstorCafeNavigation' : 'dokstorCafeNavigation'

    yield HeliocorStorage.setItem(item, JSON.stringify(navToSave))

}

function* sendDocToService({ image, extraImage, document: { name }, kind, owner }) {

    const isRequest = yield select(getIsRequest)
    const _party = yield select(getParty)
    const retries = yield select(getRetries)
    const currDocValidations = yield select(getDocValidations)

    let selfieExtraImage
    let responseVerification, documentData

    const party = owner || _party

    if (['SELFIE', 'VIDEO_SELFIE'].includes(name)) {
        const isIdentityDocument = (document) => ['PASSPORT', 'ID_CARD', 'DRIVING_LICENCE'].includes(document.type)

        let selfieExtraImageBlobUrl = party.documents.some(isIdentityDocument) && party.documents
            .filter(doc => isIdentityDocument(doc) && doc.datas && !isEmpty(doc.datas)).find(extra => !isEmpty(extra.datas))

        if (selfieExtraImageBlobUrl) {
            let selfieExtraImageWithString = yield convertBlobToBase64(selfieExtraImageBlobUrl.datas[0].content)
            selfieExtraImage = removeStringB64(selfieExtraImageWithString)
        }
    }

    documentData = yield createDocumentData({ document: { image, extraImage, selfieExtraImage, name }, party: party.data || party, kind })

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'UPLOADING' })

    responseVerification = yield verifyIndividualDocument({ documentData, name, kind })

    let properties = responseVerification?.servicesResponse?.properties

    let updateRetries = false
    let docValidations

    let allDocsToSave = []

    if (isRequest) {
        const docFound = party.documents.find(doc => doc.type === name) || { type: name, category: kind, datas: [] }
        let dataIndex = 0

        if (docFound) {
            docFound.datas.forEach(d => {
                if (d.index > dataIndex) dataIndex = d.index
            })

            const partyType = yield select(getPartyType)
            const partyPath = getPartyPath(partyType)

            if (extraImage) {

                const images = [image, extraImage]
                const docDatas = []
                for (let i = 0; i < images.length; i++) {
                    const dataResponse = owner ? false : yield db('post', `dokstor-cafe-api/api/request/${partyPath}/documents/${docFound.identifier}/datas`, { index: dataIndex + i, content: images[i] })
                    const _blob = yield b64ToBlob(images[i])
                    const _url = yield window.URL.createObjectURL(_blob)
                    if (dataResponse && dataResponse.data) {
                        docDatas.push({ identifier: dataResponse.data.identifier, content: _url, index: dataResponse.data.index, fileName: `${docFound.type} ${dataResponse.data.index}` })
                    } else if (owner) {
                        docDatas.push({ content: _url, index: i, fileName: `${docFound.type} ${i}` })
                    } else {
                        yield put({ type: SET_NOTIFICATION, notification: { text: `errors.${(dataResponse && dataResponse.errorCode) || 'oops'}`, type: 'error' } })
                    }
                }
                const newDocuments = party.documents.filter(doc => doc.type !== name)

                const res = yield documentValidator({
                    responseVerification,
                    retries,
                    docToSave: docFound,
                    name: docFound.type,
                    docValidations: currDocValidations
                })

                const { docToSave, _updateRetries, _docValidations } = res
                updateRetries = _updateRetries
                docValidations = _docValidations

                allDocsToSave = [...newDocuments, { ...docToSave, datas: [...docToSave.datas, ...docDatas], properties }]

            } else {

                const dataBody = {
                    index: dataIndex + 1,
                    content: image
                }
                const dataResponse = owner ? false : yield db('post', `dokstor-cafe-api/api/request/${partyPath}/documents/${docFound.identifier}/datas`, dataBody)
                const _blob = yield b64ToBlob(image)
                const _url = yield window.URL.createObjectURL(_blob)

                if (dataResponse && dataResponse.data) {
                    let _datas = [...docFound.datas, { identifier: dataResponse.data.identifier, content: _url, index: dataResponse.data.index, fileName: `${docFound.type} ${dataResponse.data.index}` }]
                    const newDocuments = party.documents.filter(doc => doc.type !== name)

                    const res = yield documentValidator({
                        responseVerification,
                        retries,
                        docToSave: docFound,
                        name: docFound.type,
                        docValidations: currDocValidations
                    })

                    const { docToSave, _updateRetries, _docValidations } = res
                    updateRetries = _updateRetries
                    docValidations = _docValidations

                    allDocsToSave = [...newDocuments, { ...docToSave, datas: _datas, properties }]

                } else if (owner) {

                    let _datas = [...docFound.datas, { content: _url, index: dataIndex, fileName: `${docFound.type} ${dataIndex}` }]
                    const newDocuments = party.documents.filter(doc => doc.type !== name)

                    const res = yield documentValidator({
                        responseVerification,
                        retries,
                        docToSave: docFound,
                        name: docFound.type,
                        docValidations: currDocValidations
                    })

                    const { docToSave, _updateRetries, _docValidations } = res
                    updateRetries = _updateRetries
                    docValidations = _docValidations

                    allDocsToSave = [...newDocuments, { ...docToSave, datas: _datas, properties }]

                } else {
                    yield put({ type: SET_NOTIFICATION, notification: { text: `errors.${(dataResponse && dataResponse.errorCode) || 'oops'}`, type: 'error' } })
                }
            }

            owner
                ? yield put({ type: SET_CURRENT_OWNER_DOCS, docs: allDocsToSave })
                : yield put({ type: STORE_DOCUMENTS, docs: allDocsToSave })
            
            if (updateRetries) {
                yield put({ type: SET_DOCUMENT_RETRIES, docValidations })
            }
        }
    } else {
        const blob = yield b64ToBlob(image)
        const url = yield window.URL.createObjectURL(blob)

        const datas = [{ index: 0, content: url, fileType: blob?.type }]
        const datasForLocal = [{ index: 0, content: image }]

        if (extraImage) {
            const extraBlob = yield b64ToBlob(extraImage)
            const extraUrl = yield window.URL.createObjectURL(extraBlob)
            datas[1] = { index: 1, content: extraUrl, fileType: extraBlob?.type }
            datasForLocal[1] = { index: 0, content: extraImage }
        }

        const docPreValidation = {
            type: name,
            datas,
            category: kind,
            properties: properties
        }

        const res = yield documentValidator({
            responseVerification,
            retries,
            docToSave: docPreValidation,
            name,
            docValidations: currDocValidations
        })

        const { docToSave, _updateRetries, _docValidations } = res
        updateRetries = _updateRetries
        docValidations = _docValidations

        //Saves the document in local
        if (owner) {
            yield put({ type: SET_CURRENT_OWNER_DOC, doc: docToSave })
            updateRetries = false
        } else {
            yield put({ type: STORE_DOCUMENT, document: docToSave })
        }

        if (docToSave.autoValid || docToSave.sentForManualValidation) {
            if (docToSave.sentForManualValidation) {
                yield put({ type: REQUEST_SEND_DOC_FOR_MANUAL_VALIDATION, docType: docToSave.type })
            }
            yield put({ type: SET_DOCUMENT_RETRIES, docValidations })
            yield put({ type: SAVE_DOCUMENT, doc: docToSave })
            updateRetries = false
        }

        if (updateRetries) {
            yield put({ type: SET_DOCUMENT_RETRIES, docValidations })
            yield put({ type: SAVE_DOCUMENT, doc: {} })
        }
    }

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
}

function* requestActivation({ activationCode }) {
    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    let authenticated = undefined

    const phase = yield select(getPhase)
    const isRequest = yield select(getIsRequest)

    const partyType = yield select(getPartyType)
    const email = yield select(getClientEmail)

    const url = new URL(window.location.href)
    const requestid = url.searchParams.get("request")
    const organizationId = url.searchParams.get("org")

    let details = {}

    switch (phase) {
        case phaseTypes.REQUEST:
            details = {
                organizationid: organizationId,
                requestid: requestid,
                code: activationCode,
            }
            break;
        case phaseTypes.PREAPPROVAL:
        default:
            details = {
                organizationid: organizationId,
                email: email,
                code: activationCode,
                type: partyType
            }
            break;
    }

    const apiResponse = yield db('post', `dokstor-cafe-api/api/authentication/${phase}/validate`, details)
    const responseErrors = apiResponse && apiResponse.errors && apiResponse.errors.map(err => err.code)

    const succededActivation = apiResponse.token
    yield put({ type: SET_TOKEN_VERIFICATION, activationDataChecked: true, token: apiResponse.token })

    if (!isRequest) {
        yield put({ type: REQUEST_BACKUP_PARTY })
    }

    if (succededActivation) {

        if (isRequest) yield call(runRequestRequestedEntity)
        let key = !isRequest ? 'activationData' : 'req_activationData'
        authenticated = true
        yield HeliocorStorage.setItem(key, JSON.stringify({ ...details, token: apiResponse.token, type: partyType, email }))
        yield put({ type: REQUEST_GET_SETTINGS })
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.authSuccess', type: 'success' } })

    } else if (responseErrors) {
        yield put({ type: SET_CAFE_STATUS, cafeStatus: responseErrors[0] })
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))

    } else {
        yield put({ type: SET_NOTIFICATION, notification: { text: `errors.oops`, type: 'error' } })

    }

    yield put({ type: ACTIVATE_CAFE, authenticated: authenticated })
    if (isRequest) yield put({ type: SET_CAFE_STATUS, cafeStatus: 'PARTY_LOADED' })
}

function* resetCustomerData() {
    const isRequest = yield select(getIsRequest)

    yield db('delete', `dokstor-cafe-api/api/self-onboarding`)

    yield HeliocorStorage.removeItem(isRequest ? 'req_activationData' : 'activationData')
    yield HeliocorStorage.removeItem('dokstorCafeDataBackup')
    yield HeliocorStorage.removeItem(isRequest ? 'req_dokstorCafeNavigation' : 'dokstorCafeNavigation')
    yield HeliocorStorage.removeItem('dokstorCafeDocs')
    yield put({ type: SET_SETTINGS, settings: { kycType: null } })
    yield put({ type: RESET_ACTIVATION })
    yield put({ type: REQUEST_CHECK_ACTIVATION_DATA })
}

function* sendDocForManualValidation({ docType, owner }) {
    const partyType = yield select(getPartyType)

    if (owner) {
        let documentsUpdated = cloneDeep(owner.documents) || []
        let documentToUpdateIndex = documentsUpdated.findIndex(doc => doc.type === docType)

        documentsUpdated[documentToUpdateIndex].sentForManualValidation = true
        documentsUpdated[documentToUpdateIndex].errors = ['NOT_VALIDATED']

        yield put({ type: SET_CURRENT_OWNER_DOC, doc: documentsUpdated[documentToUpdateIndex], docType: documentsUpdated[documentToUpdateIndex].type })
    } else {
        const documents = yield select(getDocuments)
        let documentsUpdated = cloneDeep(documents)
        let documentToUpdateIndex = documentsUpdated.findIndex(doc => doc.type === docType)

        documentsUpdated[documentToUpdateIndex].sentForManualValidation = true
        documentsUpdated[documentToUpdateIndex].errors = ['NOT_VALIDATED']

        if (partyType !== 'CONNECTED_PERSON' && partyType !== 'CONNECTED_COMPANY') {
            yield put({ type: SAVE_DOCUMENT, doc: documentsUpdated[documentToUpdateIndex] })
        }
        yield put({ type: STORE_DOCUMENTS, docs: documentsUpdated })
    }
}

function* storeDocument({ image, document, kind, fileName }) {

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    let existingDoc
    let dataIndex = 0

    const party = yield select(getParty)

    const partyDocuments = [...party.documents]

    const documentAlreadyExist = partyDocuments.some(doc => document.name === doc.type)

    if (documentAlreadyExist) {
        existingDoc = partyDocuments.find(doc => document.name === doc.type)
        dataIndex = existingDoc.datas.length
    }

    const blob = yield b64ToBlob(image)
    const url = yield window.URL.createObjectURL(blob)

    const datas = [{ index: dataIndex, content: url, fileName: fileName }]

    const docToSave = {
        type: document.name,
        datas: dataIndex === 0 ? datas : [...existingDoc.datas, ...datas],
        category: kind
    }

    if (documentAlreadyExist) {
        let indexOfExistingDoc = partyDocuments.findIndex(doc => document.name === doc.type)
        partyDocuments[indexOfExistingDoc] = docToSave
    } else {
        partyDocuments.push(docToSave)
    }

    yield put({ type: SAVE_DOCUMENT, doc: docToSave })
    yield put({ type: STORE_DOCUMENTS, docs: partyDocuments })
    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
}

function* sendActivationEmail({ email, partyType }) {

    let details = {}

    yield put({ type: SET_CLIENT_EMAIL, email })

    const phase = yield select(getPhase)
    const url = new URL(window.location.href)
    const requestid = url.searchParams.get("request")
    const organizationId = url.searchParams.get("org")

    const requestIdIsValid = requestid && requestid.length === 32
    const requestOrgIsvalid = organizationId && organizationId.length === 32

    const shouldWeSendEmail = phase === phaseTypes.REQUEST ? requestIdIsValid && requestOrgIsvalid : requestOrgIsvalid

    if (shouldWeSendEmail) {
        switch (phase) {
            case phaseTypes.PREAPPROVAL:
                details = {
                    organizationid: organizationId,
                    email: email,
                    type: partyType
                }
                break;
            case phaseTypes.REQUEST:
                details = {
                    organizationid: organizationId,
                    requestid: requestid
                }
                break;
            default:
                details = {}
                break;
        }

        const apiResponse = yield db('post', `dokstor-cafe-api/api/authentication/${phase}/activate`, details)
        const responseErrors = apiResponse && apiResponse.errors && apiResponse.errors.map(err => err.code)

        if (responseErrors) {
            yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
            yield put({ type: SET_CAFE_STATUS, cafeStatus: responseErrors[0] })
        } else {
            yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
            yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.emailActivation', type: 'info' } })
        }
    } else {
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LINK_NOT_VALID' })
        yield put({ type: SET_NOTIFICATION, notification: { text: 'errors.LINK_NOT_VALID', type: 'error' } })
        yield put({ type: ACTIVATE_CAFE, authenticated: undefined })
    }
}

function* sendDataPreApproval() {
    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const partyType = yield select(getPartyType)

    let typeUrl = partyType === 'PERSON' ? 'persons' : 'companies'

    const response = yield db('post', `dokstor-cafe-api/api/self-onboarding/${typeUrl}`)

    const hasPostErrors = response?.errors?.some(e => e.field)
    const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

    if (hasPostErrors) {
        yield put({ type: SET_POST_ERRORS, errors: response.errors })
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'POST_ERRORS' })
    } else if (responseErrors) {
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'NOT_SUBMITTED' })
    } else {
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.successSentAll', type: 'success' } })
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'SUBMITTED' })
        yield HeliocorStorage.clear()
    }
}

function* checkPhase() {

    yield put({ type: SET_INIT_APP, appIsReady: false })

    let phase, isRequest
    const url = new URL(window.location.href)
    const request = url.searchParams.get("request")
    const org = url.searchParams.get("org")

    if (!org) {
        phase = phaseTypes.UNKNOWN_ORG
        yield put({ type: SET_PHASE_TYPE, phase, org })
        return yield put({ type: SET_INIT_APP, appIsReady: true })
    } else if (request) {
        phase = phaseTypes.REQUEST
        isRequest = true
    } else {
        isRequest = false
        phase = phaseTypes.PREAPPROVAL
    }

    yield put({ type: SET_PHASE_TYPE, phase, org })
    yield put({ type: SET_IS_REQUEST, isRequest })
    yield put({ type: REQUEST_CHECK_ACTIVATION_DATA, phase })
}

function* checkActivationData({ phase }) {
    switch (phase) {
        case phaseTypes.REQUEST:
            yield put({ type: REQUEST_INIT_REQUEST })
            break;
        case phaseTypes.PREAPPROVAL:
            yield put({ type: REQUEST_INIT_PREAPPROVAL })
            break;
        default:
    }
}

function* preApprovalInit() {

    let tokenIsValid = false
    let apiResponse = {}
    let token, partyType, email
    const authenticated = yield select(getAuthenticationStatus)
    const org = yield select(getOrg)
    let activationData = yield HeliocorStorage.getItem('activationData')
    activationData = yield JSON.parse(activationData)

    const url = new URL(window.location.href)
    const type = url.searchParams.get("type")

    const typeIsCorrect =  type ? activationData?.type === type : true
    const orgIsTheSameAsActivationData = activationData?.organizationid === org

    if (orgIsTheSameAsActivationData && typeIsCorrect) {
        token = activationData.token
        apiResponse = yield db('post', 'dokstor-cafe-api/api/authentication/self-onboarding/status', { token })

        const apiResponseErrors = apiResponse && ((apiResponse.errors && apiResponse.errors.map(err => err.code)) || apiResponse.errorCode)

        partyType = activationData.type
        yield put({ type: SET_ONBOARDING_TYPE, partyType })

        email = activationData.email
        tokenIsValid = apiResponse && !apiResponse.errors

        if (!tokenIsValid) {
            yield put({ type: REQUEST_RESET_CUSTOMER_DATA })
            yield all(apiResponseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        } else {
            yield put({ type: SET_TOKEN_VERIFICATION, activationDataChecked: true, token })
        }

        yield put({ type: REQUEST_BACKUP_PARTY })
        
    } else {
        yield HeliocorStorage.removeItem('activationData')
        yield HeliocorStorage.removeItem('dokstorCafeDataBackup')
        yield HeliocorStorage.removeItem('dokstorCafeNavigation')
        yield HeliocorStorage.removeItem('dokstorCafeDocs')
    }

    if (tokenIsValid && !authenticated) {
        yield put({ type: REQUEST_GET_SETTINGS, partyType })
        yield put({ type: ACTIVATE_CAFE, authenticated: tokenIsValid })
        yield put({ type: SET_CLIENT_EMAIL, email })
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.authSuccess', type: 'success' } })
    } else {
        yield put({ type: ACTIVATE_CAFE, authenticated: tokenIsValid })
        yield put({ type: REQUEST_GET_PERSONALIZATION, orgId: org })
    }
}

//#region REQUEST 

function* requestInit() {

    let tokenIsValid = false
    let apiResponse = {}
    let token, partyType, email, requestId_stored, requestIdIsValid, requestIsDifferent
    const authenticated = yield select(getAuthenticationStatus)
    let req_activationData = yield HeliocorStorage.getItem('req_activationData')
    let req_dokstorCafeNavigation = yield HeliocorStorage.getItem('req_dokstorCafeNavigation')
    const org = yield select(getOrg)
    const url = new URL(window.location.href)
    const requestid = yield url.searchParams.get("request")

    req_activationData = yield JSON.parse(req_activationData)
    req_dokstorCafeNavigation = yield JSON.parse(req_dokstorCafeNavigation)
    requestIsDifferent = req_activationData && req_activationData.requestid !== requestid

    if (req_activationData && !requestIsDifferent) {

        token = req_activationData.token
        requestId_stored = req_activationData.requestid
        let requestIs32Character = requestid.length === 32

        requestIdIsValid = requestId_stored === requestid || requestIs32Character

        if (requestIdIsValid) {
            apiResponse = yield db('post', 'dokstor-cafe-api/api/authentication/request/status', { token })
            if (!apiResponse?.errors) {
                partyType = req_activationData.type
                email = req_activationData.email
                tokenIsValid = !apiResponse.errorCode
            } else {
                tokenIsValid = false
            }
        } else {
            tokenIsValid = false
        }

        yield put({ type: SET_ONBOARDING_TYPE, partyType })

        if (tokenIsValid) {
            yield put({ type: SET_TOKEN_VERIFICATION, activationDataChecked: true, token })
            if (req_dokstorCafeNavigation) yield put({ type: SET_NAVIGATION, navigation: req_dokstorCafeNavigation })
            yield call(runRequestRequestedEntity)
        } else {
            yield put({ type: REQUEST_RESET_CUSTOMER_DATA })
        }
    } else {
        yield HeliocorStorage.removeItem('req_activationData')
        yield HeliocorStorage.removeItem('req_dokstorCafeNavigation')
    }

    if (tokenIsValid && !authenticated) {
        yield put({ type: REQUEST_GET_SETTINGS, partyType })
        yield put({ type: ACTIVATE_CAFE, authenticated: tokenIsValid })
        yield put({ type: SET_CLIENT_EMAIL, email })
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.authSuccess', type: 'success' } })
    } else if (requestIdIsValid === false) {
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'REQUEST_NOT_VALID' })
        yield put({ type: SET_NOTIFICATION, notification: { text: 'errors.REQUEST_NOT_VALID', type: 'error' } })
        yield put({ type: ACTIVATE_CAFE, authenticated: undefined })
    } else {
        yield put({ type: ACTIVATE_CAFE, authenticated: undefined })
        yield put({ type: REQUEST_GET_PERSONALIZATION, orgId: org, authenticated: tokenIsValid })
    }
}

function flattenStructure(persons, companies, orig = [], level = 0) {

    const mapPersons = (entities, level) => entities.map(e => ({ ...e, level, type: 'PERSON' }))

    const origWithPersons = [...orig, ...mapPersons(persons, level)]

    return companies.reduce((acc, cur) => {
        const { connectedPersons = [], connectedCompanies = [], ...rest } = cur

        return connectedCompanies?.length > 0 || connectedPersons?.length > 0
            ? flattenStructure(connectedPersons, connectedCompanies, [...acc, { ...rest, level, type: 'COMPANY' }], level + 1)
            : [...acc, ...mapPersons(connectedPersons, level + 1), { ...cur, level, type: 'COMPANY' }]
    }, origWithPersons)
}

const parseCustomStrings = fields => {
    const custom = {}
    for (let f in fields) {
        try {
            custom[f] = JSON.parse(fields[f])
        } catch {
            custom[f] = fields[f]
        }
    }
    return custom
}

function* runRequestRequestedEntity() {

    const partyType = yield select(getPartyType)
    const partyPath = getPartyPath(partyType)
    const isCompany = partyType === "COMPANY"

    const response = yield db('get', `dokstor-cafe-api/api/request/${partyPath}`)
    const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

    let documents
    let docsToSave = []

    if (responseErrors || !response) {
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
    } else {
        const partyResponse = response?.party || {}

        documents = partyResponse?.documents

        if (isCompany) {
            const { documents, connectedCompanies = [], connectedPersons = [], ...rest } = partyResponse
            const { custom, ...base } = rest
            yield put({ type: STORE_PARTY_DATA, party: { data: { ...base, ...parseCustomStrings(custom) }, backupPartyLoaded: true } })
            const owners = flattenStructure(connectedPersons, connectedCompanies)

            yield put({ type: SET_OWNERS, owners })
        } else {
            const { documents, ...rest } = partyResponse
            const { custom, ...base } = rest
            yield put({ type: STORE_PARTY_DATA, party: { data: { ...base, ...parseCustomStrings(custom) }, backupPartyLoaded: true } })
        }
    }

    if (!documents) {
        yield put({ type: SET_NOTIFICATION, notification: { text: 'errors.oops', type: 'error' } })
    } else {
        for (let i = 0; i < documents.length; i++) {
            const data = yield db('get', `dokstor-cafe-api/api/request/${partyPath}/documents/${documents[i].identifier}/datas`)
            if (data.datas) {
                const newData = data.datas.map(d => {
                    const blob = b64ToBlob(d.content)
                    const url = window.URL.createObjectURL(blob)
                    return { identifier: d.identifier, index: d.index, content: url, fileName: `${documents[i].type} ${d.index}` }
                })
                docsToSave.push({ ...documents[i], datas: newData })
            }
        }
        yield put({ type: STORE_DOCUMENTS, docs: docsToSave })
    }
}

function* runRequestUploadEntityDocuments({ kind, document, image }) {

    const _partyType = yield select(getPartyType)
    const partyType = getPartyPath(_partyType)

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const documents = yield select(getDocuments)
    const docUploaded = documents.find(doc => doc.type === document.name)

    let dataIndex = 0
    let datas = []
    let dataBody = {}

    const blob = yield b64ToBlob(image)
    const url = yield window.URL.createObjectURL(blob)

    if (docUploaded) {
        docUploaded.datas.forEach(d => {
            if (d.index > dataIndex) dataIndex = d.index
        })
        dataBody = {
            index: dataIndex + 1,
            content: image
        }
        const dataResponse = yield db('post', `dokstor-cafe-api/api/request/${partyType}/documents/${docUploaded.identifier}/datas`, dataBody)

        if (dataResponse && dataResponse.data) {
            datas = [...docUploaded.datas, { identifier: dataResponse.data.identifier, content: url, index: dataResponse.data.index, fileName: `${docUploaded.type} ${dataResponse.data.index}` }]
            const newDocuments = documents.filter(doc => doc.type !== document.name)
            yield put({ type: STORE_DOCUMENTS, docs: [...newDocuments, { ...docUploaded, datas }] })
        } else {
            yield put({ type: SET_NOTIFICATION, notification: { text: `errors.${(dataResponse && dataResponse.errorCode) || 'oops'}`, type: 'error' } })
        }
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
    } else {
        const body = {
            category: kind,
            type: document.name
        }
        dataBody = {
            index: dataIndex,
            content: image
        }
        const doc = yield db('post', `dokstor-cafe-api/api/request/${partyType}/documents`, body)

        if (!doc || doc.errorCode) {
            yield put({ type: SET_NOTIFICATION, notification: `errors.${documents.errorCode}`, color: 'red100' })
        } else {
            const dataResponse = yield db('post', `dokstor-cafe-api/api/request/${partyType}/documents/${doc.document.identifier}/datas`, dataBody)
            if (dataResponse && dataResponse.data) {
                datas = [{ identifier: dataResponse.data.identifier, content: url, index: dataResponse.data.index, fileName: `${doc.document.type} ${dataResponse.data.index}` }]
                const docs = [...documents, { ...doc.document, datas }]
                yield put({ type: STORE_DOCUMENTS, docs })
            }
            yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
        }
    }
}

function* runRequestOwners() {

    const response = yield db('get', `dokstor-cafe-api/api/request/companies`)

    if ((response && (response.errorCode || response.errors)) || !response) {
        yield put({ type: SET_NOTIFICATION, notification: { text: `errors.${(response && (response.errorCode || response.errors.map(err => err.code)[0])) || 'oops'}`, type: 'error' } })
    } else {
        const { connectedCompanies = [], connectedPersons = [] } = response.party
        const owners = flattenStructure(connectedPersons, connectedCompanies)

        yield put({ type: SET_OWNERS, owners })
    }
}

function* runRequestInsertOwner({ newEntityType, owner, comeFromLookup }) {

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'CREATING_OWNER' })

    const isRequest = yield select(getIsRequest)
    const fields = yield select(getCPFields)
    const isCompany = newEntityType === 'COMPANY'
    const partyFields = isCompany ? fields.CONNECTED_COMPANY : fields.CONNECTED_PERSON

    const _owner = cloneDeep(owner)
    delete _owner.confirmEmail
    delete _owner.type
    delete _owner.identifier
    delete _owner.relationStatus
    delete _owner.minDocsNotReached
    delete _owner.level
    delete _owner.region

    if (isCompany) delete _owner.authorizedSignatory

    if (_owner.documents) {
        _owner.documents = _owner.documents.filter(doc => ((doc.datas && !isEmpty(doc.datas)) || doc.request))
        for (let i = 0; i < _owner.documents.length; i++) {
            delete _owner.documents[i].identifier
            delete _owner.documents[i].requestStatus
            delete _owner.documents[i].properties
            delete _owner.documents[i].errors
            delete _owner.documents[i].sentForManualValidation
            delete _owner.documents[i].reasons
            delete _owner.documents[i].autoValid
            for (let j = 0; j < _owner.documents[i]?.datas?.length; j++) {
                const content = yield convertBlobToBase64(_owner.documents[i].datas[j].content)
                _owner.documents[i].datas[j].content = removeStringB64(content)
                _owner.documents[i].datas[j].index = j
                delete _owner.documents[i].datas[j].fileName
                delete _owner.documents[i].datas[j].fileType
                delete _owner.documents[i].datas[j].category
            }
        }
    }

    let endpoint, response

    if (isRequest) {

        endpoint = isCompany
            ? `dokstor-cafe-api/api/request/companies/connected-companies`
            : `dokstor-cafe-api/api/request/companies/connected-persons`

        response = yield db('post', endpoint, parserCPValues(partyFields, _owner))

        if (!response.errors && response) {
            yield call(runRequestOwners)
        }
 
    } else {

        endpoint = isCompany
            ? `dokstor-cafe-api/api/self-onboarding/companies/connected-companies`
            : `dokstor-cafe-api/api/self-onboarding/companies/connected-persons`
        
        delete owner.region

        //#start: recursively calculate ownership
        const cps = yield select(getCPs)
        let currentOwnership = 0

        const getOwnership = parentId => {
            if (parentId === 'parent') return owner.ownership
            else {
                const parentOwnership = cps.find(cp => cp.identifier === parentId).ultimateOwnership
                return (owner.ownership * parentOwnership)/100 
            }
        }

        currentOwnership = getOwnership(owner.parentId)
        //#end

        const _fields = parserCPValues(partyFields, _owner)
        const data = {
            ..._fields,
            parentId: owner.parentId === 'parent' ? null : owner.parentId
        }

        response = yield db('post', endpoint, data)

        yield put({ type: 'STORE_OWNER', owner: { ...owner, identifier: response.identifier, ultimateOwnership: currentOwnership, type: newEntityType } })
    }

    const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

    if (responseErrors || !response) {
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
    } 

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'OWNER_CREATED' })
    if (comeFromLookup) yield put({ type: DELETE_LOOKUP_SUGGESTION, owner })

}

function* runRequestUpdateOwner({ owner }) {

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'UPDATING_OWNER' })
    const isRequest = yield select(getIsRequest)
    const fields = yield select(getCPFields)
    const isCompany = owner && owner.type === 'COMPANY'
    const partyFields = isCompany ? fields.CONNECTED_COMPANY : fields.CONNECTED_PERSON

    const entityType = isCompany
        ? 'connectedCompany'
        : 'connectedPerson'
    
    const _owner = cloneDeep(owner)
    delete _owner.confirmEmail
    delete _owner.type
    delete _owner.identifier
    delete _owner.relationStatus
    delete _owner.minDocsNotReached
    delete _owner.status
    delete _owner.level
    delete _owner.ultimateOwnership

    if (isCompany) {
        delete _owner.authorizedSignatory
        delete _owner.connectedCompanies
        delete _owner.connectedPersons
    }

    let response, endpoint

    if (_owner.documents) {
        _owner.documents = _owner.documents.filter(doc => ((doc.datas && !isEmpty(doc.datas)) || doc.request))
        for (let i = 0; i < _owner.documents.length; i++) {
            delete _owner.documents[i].identifier
            delete _owner.documents[i].requestStatus
            delete _owner.documents[i].properties
            delete _owner.documents[i].errors
            delete _owner.documents[i].sentForManualValidation
            delete _owner.documents[i].reasons
            delete _owner.documents[i].autoValid
            for (let j = 0; j < _owner.documents[i]?.datas?.length; j++) {
                const content = yield convertBlobToBase64(_owner.documents[i].datas[j].content)
                _owner.documents[i].datas[j].content = removeStringB64(content)
                delete _owner.documents[i].datas[j].fileName
                delete _owner.documents[i].datas[j].category
                delete _owner.documents[i].datas[j].fileType
            }
        }
    }

    if (isRequest) {
        endpoint = isCompany
            ? `dokstor-cafe-api/api/request/companies/connected-companies/${owner.identifier}`
            : `dokstor-cafe-api/api/request/companies/connected-persons/${owner.identifier}`

        response = yield db('put', endpoint, parserCPValues(partyFields, _owner))
    } else {
        endpoint = isCompany
            ? `dokstor-cafe-api/api/self-onboarding/companies/connected-companies/${owner.identifier}`
            : `dokstor-cafe-api/api/self-onboarding/companies/connected-persons/${owner.identifier}`
        
        response = yield db('put', endpoint, parserCPValues(partyFields, _owner))
    }

    const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

    if (responseErrors || !response) {
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
    } else {
        delete owner.minDocsNotReached

        yield put({
            type: UPDATE_OWNERS, entity: {
                ...owner,
                documents: (response[entityType] && response[entityType].documents) || owner.documents
            }
        })
    }

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'OWNER_UPDATED' })
}

function* runRequestDeleteOwner({ owner }) {

    const isRequest = yield select(getIsRequest)
    const isCompany = owner && owner.type === 'COMPANY'

    if (isRequest) {

        const endpoint = isCompany
            ? `dokstor-cafe-api/api/request/companies/connected-companies/${owner.identifier}`
            : `dokstor-cafe-api/api/request/companies/connected-persons/${owner.identifier}`

        const response = yield db('delete', endpoint)
        const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

        if (responseErrors || !response) {
            yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        } else {
            yield put({ type: DELETE_ENTITY, identifier: owner.identifier })
            yield put({ type: SET_CAFE_STATUS, cafeStatus: 'OWNER_DELETED' })
        }
    } else {

        const endpoint = isCompany
            ? `dokstor-cafe-api/api/self-onboarding/companies/connected-companies/${owner.identifier}`
            : `dokstor-cafe-api/api/self-onboarding/companies/connected-persons/${owner.identifier}`

        const response = yield db('delete', endpoint)
        const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

        if (responseErrors || !response) {
            yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        } else {
            yield put({ type: DELETE_ENTITY, identifier: owner.identifier })
            yield put({ type: SET_CAFE_STATUS, cafeStatus: 'OWNER_DELETED' })
        }
    }
}

function* runRequestDatasFromOwnerDocs({ owner }) {

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING_OWNER_DOCS' })

    const ownerDocs = owner.documents || []
    const ownerType = owner.type === 'COMPANY' ? 'connected-companies' : 'connected-persons'

    const docsToSave = []

    if (owner.type === 'PERSON') {
        for (let i = 0; i < ownerDocs.length; i++) {
            const data = yield db('get', `dokstor-cafe-api/api/request/companies/${ownerType}/${owner.identifier}/documents/${ownerDocs[i].identifier}/datas`)
            if (data.datas) {
                const newData = data.datas.map(d => {
                    const blob = b64ToBlob(d.content)
                    const url = window.URL.createObjectURL(blob, { type: blob.type })
                    return { index: d.index, content: url, fileName: `${ownerDocs[i].type} ${d.index}` }
                })
                docsToSave.push({ ...ownerDocs[i], datas: newData })
            }
        }
        yield put({ type: SET_CURRENT_OWNER_DOCS, docs: docsToSave })
    }

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'READY' })
}

function* runUpdateOwner({ owner }) {

    const isRequest = yield select(getIsRequest)

    const { parentId, ..._owner } = owner

    if (isRequest) {
        const ownerType = owner.type === 'COMPANY' ? 'connected-companies' : 'connected-persons'
        const endpoint = `dokstor-cafe-api/api/request/companies/${ownerType}/${owner.identifier}/documents`

        const docsToDelete = yield select(getCurrentOwnerDocsToDelete)
        const docIdsToDelete = docsToDelete
            .filter(doc => doc && doc.identifier)
            .map(doc => doc.identifier)

        let response

        if (!isEmpty(docIdsToDelete)) {
            response = yield db('delete', endpoint, { documentsIdentifiers: docIdsToDelete })
        } else {
            response = true
        }

        const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

        if (responseErrors || !response) {
            yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        } else {
            yield call(runRequestUpdateOwner, { owner: _owner })
        }
    } else {
        yield call(runRequestUpdateOwner, { owner: _owner })
    }
}


function* runRequestUpdateRequestedEntity({ values }) {

    const party = yield select(getParty)
    const partyType = yield select(getPartyType)
    const fields = yield select(getFields)
    const partyPath = getPartyPath(partyType)

    const body = parserValues(fields, values)

    const endpoint = `dokstor-cafe-api/api/request/${partyPath}`
    const response = yield db('put', endpoint, body)

    if ((response && (response.errorCode || response.errors)) || !response) {
        yield put({ type: SET_NOTIFICATION, notification: { text: `errors.${(response && (response.errorCode || response.errors.map(err => err.code)[0])) || 'oops'}`, type: 'error' } })
    } else {

        const endpoint = `dokstor-cafe-api/api/request/${partyPath}`
        const response = yield db('get', endpoint)

        const entityData = response?.party|| {}
        const { custom, ...base } = entityData

        const partyToSave = {
            ...party,
            data: {
                ...party.data,
                ...base,
                ...parseCustomStrings(custom)
            }
        }

        yield put({ type: STORE_PARTY_DATA, party: partyToSave })
    }
}

function* runRequestSubmitRequest() {

    const partyType = yield select(getPartyType)
    const partyPath = getPartyPath(partyType)

    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const response = yield db('post', `dokstor-cafe-api/api/request/${partyPath}/submit`)
    const responseErrors = response && ((response.errors && response.errors.map(err => err.code)) || response.errorCode)

    if (responseErrors || !response) {
        yield all(responseErrors.map(err => put({ type: SET_NOTIFICATION, notification: { text: `warning.${err}`, type: 'error' } })))
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'tryAgain' })
    } else {
        yield put({ type: SET_NOTIFICATION, notification: { text: 'warning.successSentAll', type: 'success' } })
        yield put({ type: SET_CAFE_STATUS, cafeStatus: 'SUBMITTED' })
        HeliocorStorage.clear()
    }
}

function* requestBackupParty() {

    const _partyType = yield select(getPartyType)
    const isCompany = _partyType === "COMPANY" || _partyType === "CONNECTED_COMPANY"
    const partyType = isCompany  ? 'companies' : 'persons'
    
    yield put({ type: SET_CAFE_STATUS, cafeStatus: 'LOADING' })

    const backup = yield db('get', `dokstor-cafe-api/api/self-onboarding/${partyType}`)

    const entityBU = backup.party

    const url = new URL(window.location.href)
    const consent = url.searchParams.get("consent")

    if (!isEmpty(entityBU)) {
        let ownerPersonsToSave = []
        let ownerCompaniesToSave = []

        const { navigation } = backup
        const { connectedPersons = [], connectedCompanies = [], custom, documents, kycType, ...data } = entityBU
        const connectedParties = [...connectedPersons, ...connectedCompanies]

        //TODO: #region FIND A METHOD TO AVOID 3 CONSECUTIVES MAPS TODO... 
        if (!isEmpty(connectedPersons)) {
            ownerPersonsToSave = yield all(connectedPersons.map(async ccOwner => {
                const {custom, ...owner} = ccOwner
                let level = 0;
                let ultimateOwnership = owner.ownership
                if(owner.parentId) {
                    const parent = connectedParties.find(p => p.identifier === owner.parentId)
                    ultimateOwnership = (owner.ownership * parent.ownership)/100
                    level = parent ? (parent.level || 0) + 1 : 0
                }
                if (owner.documents) {
                    const documents = await Promise.all(owner.documents.map(async doc => {
                        if (doc.datas) {
                            const datas = await Promise.all(doc.datas.map(async data => {
                                const blob = b64ToBlob(data.content)
                                const url = window.URL.createObjectURL(blob, { type: blob.type })
                                return { index: data.index, content: url }
                            }))
                            return { ...doc, datas }
                        }
                        return doc
                    }))
                    return { ...owner, documents, type: 'PERSON', level, ultimateOwnership, ...custom }
                }
                return { ...owner, level, ultimateOwnership, ...custom }
            }))
        }

        if (!isEmpty(connectedCompanies)) {
            ownerCompaniesToSave = yield all(connectedCompanies.map(async ccOwner => {
                const {custom, ...owner} = ccOwner
                let level = 0;
                let ultimateOwnership = owner.ownership
                if(owner.parentId) {
                    const parent = connectedPersons.find(p => p.identifier === owner.parentId)
                    ultimateOwnership = (owner.ownership * (parent.ownership))/100
                    level = (parent.level || 0) + 1 
                }
                if (owner.documents) {
                    const documents = await Promise.all(owner.documents.map(async doc => {
                        if (doc.datas) {
                            const datas = await Promise.all(doc.datas.map(async data => {
                                const blob = b64ToBlob(data.content)
                                const url = window.URL.createObjectURL(blob, { type: blob.type })
                                return { index: data.index, content: url }
                            }))
                            return { ...doc, datas }
                        }
                        return doc
                    }))
                    return { ...owner, documents, type: 'COMPANY', level, ultimateOwnership, ...custom }
                }
                return { ...owner, level, ultimateOwnership, ...custom }
            }))
        }
        //#endregion

        let _documents
        if (documents) {
            _documents = yield all(documents.map(async doc => {
                const { datas, metadata } = doc
                if (datas) {
                    const datas = await Promise.all(doc.datas.map(async data => {
                        const blob = b64ToBlob(data.content)
                        const url = window.URL.createObjectURL(blob)
                        return { index: data.index, content: url, fileType: blob?.type }
                    }))
                    return { ...doc, datas, ...(metadata ? JSON.parse(metadata) : {})}
                }
                return doc
            }))
        }

        let _custom = {}
        for (const field in custom) {
            try {
                _custom[field] = JSON.parse(custom[field])
            } catch {
                _custom[field] = custom[field]
            }
        }

        if (kycType) {
            yield put({ type: SET_SETTINGS, settings: { kycType } })
            yield put({ type: REQUEST_CUSTOM_FIELDS,  kycTypeId: kycType  })
            yield put({ type: REQUEST_DOCUMENTS, kycTypeId: kycType })
        }

        const _nav = (navigation && JSON.parse(navigation)) || {}
        const { docValidations, ...rest } = _nav
    

        yield put({
            type: SET_BACKUP_PARTY,
            party: {
                data: { ...data, ..._custom },
                documents: _documents,
                connectedParties: [...ownerPersonsToSave, ...ownerCompaniesToSave],
                consent,
                backupPartyLoaded: true,
                docValidations: docValidations || {}
            },
            navigation: rest
        })
    } 
}


function* runRequestUpdateKycType({kycType, onSuccess}) {
        yield requestCustomFields({kycTypeId: kycType})
        yield requestDocuments({kycTypeId: kycType})
        yield put({ type: SET_SETTINGS, settings: {kycType} })
        onSuccess()
}

export default function* rootSaga() {
    yield all([fork(watcher())])
}